/* Core & libraries */
import React, { useEffect, useState } from "react";
import { useTheme } from "react-jss";
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from "react-intl";
import moment from "helpers/moment";
import { NumericFormat } from 'react-number-format';
import { Tooltip, OverlayTrigger} from "react-bootstrap";

/* Helpers, constants, custom methods, internal libraries */
import { useLocaleContext } from "../../../../contexts/localeContext";
import { useTravelCreditsService } from "../../../../services/travelCreditsService.js";
import { PAGE_TITLES } from "../../../../constants/appConstants";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { nameInitials, getCurrencySymbol } from "../../../../helpers/general";

/* Components */
import UCGrid from "../../../ucgrid/index.js";
import GridNoData from "../../../gridNoData"
import GridSearchNoData from "../../../gridSearchNoData";
import { ReactComponent as CreditsIcon } from "../../../../resources/images/aa-credits-rounded.svg"
import { ReactComponent as HelpIcon } from "../../../../resources/images/help.svg";
import { ReactComponent as SearchIcon } from "../../../../resources/images/search-grid-rounded.svg";


/* Styles */
import styles from "./styles.js"

const ExpiredGrid = ({gridOptions, setGridOptions, query, clearSearch, setEnableDownloadButton}) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const navigate = useNavigate();
  const globalLocale = useLocaleContext();
  const { getTravelCreditsList, travelCreditsResponse } = useTravelCreditsService();
  const { initAnalytics } = useAnalytics();

  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["expiredCredits"]
    };
    initAnalytics(initUtagData, 'init call', 'view')
  }, []);

  useEffect(() => {
    getTravelCreditsList(gridOptions, "EXPIRED" , "expiredGridSpinner");
    gridOptions.page > 1 ? navigate(`/app/travelcredits/expired?page=${gridOptions.page}`) : navigate(`/app/travelcredits/expired`)
  }, [gridOptions]);

  useEffect(() => {
    if(travelCreditsResponse?.results?.dataItems.length > 0) {
      setEnableDownloadButton(true)
    } else {
      setEnableDownloadButton(false)
    }
  }, [travelCreditsResponse]);

  const onRowClick = row => {}

  return (
    <UCGrid
      gridStyle={classes.expiredGrid}
      gridName="expiredGrid"
      data={ travelCreditsResponse?.results }
      columns={ travelCreditsResponse?.columns }
      actions={ travelCreditsResponse?.actions }
      actionsWidth={"auto"}
      handlePageChange={(page) => {setGridOptions({
        ...gridOptions,
        page
      })
      page > 1 ? navigate(`/app/travelcredits/expired?page=${page}`) : navigate(`/app/travelcredits/expired`)
      }}
      setSelectedRow={setSelectedRow}
      spinner="expiredGridSpinner"
      customColumns={{
        assignedTo: (row) => (
          <div>
          {
            row.assignedTo ? 
            <div className={classes.link}>
              <div className={classes.nameWrapper}>
                <div className={classes.initialsAvatar}>
                  <span>{ nameInitials(row?.assignedTo) }</span>
                </div>
                <div className={classes.details}>
                  <p>{row?.assignedTo}</p>
                  <p>{ row?.email ? row.email : "-" }</p>
                </div>
              </div>
            </div>
            : <span className={classes.unassigned}><FormattedMessage id="expiredGrid.unassigned"/></span>
          }
          </div>
        ),
        value: (row) => {
        return (
          <NumericFormat
            value={row?.value}
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
            displayType="text"
            prefix={getCurrencySymbol(row?.currency)}
            renderText={(value) => <div>{value} {row?.currency}</div>}
          />
         )
        },
        expiryDate: (row) => {
          const isExpiringSoon = moment(row?.expiryDate).diff(moment(), 'days') >= 0 && moment(row?.expiryDate).diff(moment(), 'days') < 90;
          return (
            <div className={isExpiringSoon ? classes.expiringSoon : classes.number}> {moment(row?.expiryDate).locale(globalLocale).format("DD MMM YYYY") } </div>
          )
        },
        issueDateTime: (row) => (
          <div className={classes.link}>
            {row?.issueDateTime && row.issueDateTime !== "0000-00-00 00:00:00"
              ? moment(row.issueDateTime).locale(globalLocale).format("DD MMM YYYY")
              : "-"
            }
          </div>
        ),
      }}
      columnCustomWidth={{
        number: "200px !important",
        ticketNumber: "200px !important",
        value: "200px !important",
        assignedTo: "356px !important",
        expiryDate: "200px !important",
        originalTravelerName: "250px !important"
      }}
      columnCustomProps={[
        {
          columnName: "assignedTo",
          tooltip: true,
          component: (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="tooltip" className={classes.tooltip}>
                  <FormattedMessage id="expiredGrid.tooltipText"/>
                </Tooltip>
              }
            >
              <HelpIcon className={classes.helpIcon}/>
            </OverlayTrigger>
          )
        },
        {
          columnName: "originalTravelerName",
          tooltip: true,
          component: (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="originalTravelerNameTooltip" className={classes.tooltip}>
                  <FormattedMessage id="tripCredit.previousTravelerToolTip" />
                </Tooltip>
              }
            >
              <HelpIcon className={classes.helpIcon}/>
            </OverlayTrigger>
          )
        },
        {
          columnName: "ticketNumber",
          tooltip: true,
          component: (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="tooltip" className={classes.tooltip}>
                  <FormattedMessage id="tripCredit.ticketToolTip"/>
                </Tooltip>
              }
            >
              <HelpIcon className={classes.helpIcon}/>
            </OverlayTrigger>
          )
        },
      ]}
      noDataComponent={
        query ?
          <GridSearchNoData 
            icon={<SearchIcon />} 
            title={<FormattedMessage id="travelcredits.search.noResults"/>} 
            buttonText={<FormattedMessage id="travelcredits.search.clear"/>}
            clearSearch={clearSearch}
            spinner="expiredGridSpinner"
          /> 
        :
          <GridNoData 
            icon={<CreditsIcon />} 
            title={<FormattedMessage id="expiredGrid.noDataTitle"/>} 
            description={<FormattedMessage id="expiredGrid.noDataDescription"/>}
          /> 
      }
      onRowClick={onRowClick}
      defaultSort={travelCreditsResponse?.defaultSort}
      handleSort={(column, sortDirection) =>
        setGridOptions({
          ...gridOptions,
          sortby: column.id,
          sortdirection: sortDirection === "desc" ? "dsc" : sortDirection,
        })
      }
    />
  )
}

export default ExpiredGrid;